import { useCallback, useRef } from 'react';

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  key: string;
}

interface ChartCacheOptions {
  maxAge?: number; // Cache expiry in milliseconds (default: 5 minutes)
  maxEntries?: number; // Maximum cache entries (default: 50)
}

/**
 * Hook for caching chart data to improve performance when staying on the same machine
 * Caches data based on machineSerialNo + dateRange + endpoint combination
 */
export const useChartCache = <T>({ 
  maxAge = 5 * 60 * 1000, // 5 minutes default
  maxEntries = 50 
}: ChartCacheOptions = {}) => {
  const cache = useRef<Map<string, CacheEntry<T>>>(new Map());

  // Generate cache key from parameters
  const generateKey = useCallback((
    endpoint: string,
    machineSerialNo: string,
    dateRange: { start: string; end: string },
    shifts: string[]
  ): string => {
    const shiftsKey = shifts.sort().join(',') || 'all';
    return `${endpoint}:${machineSerialNo}:${dateRange.start}:${dateRange.end}:${shiftsKey}`;
  }, []);

  // Get cached data
  const getCachedData = useCallback((
    endpoint: string,
    machineSerialNo: string,
    dateRange: { start: string; end: string },
    shifts: string[]
  ): T | null => {
    if (!machineSerialNo || !dateRange.start || !dateRange.end) return null;

    const key = generateKey(endpoint, machineSerialNo, dateRange, shifts);
    const entry = cache.current.get(key);

    if (!entry) {
      console.log(`🗂️ Cache MISS for ${endpoint} - ${machineSerialNo}`);
      return null;
    }

    // Check if cache entry is still valid
    const now = Date.now();
    if (now - entry.timestamp > maxAge) {
      console.log(`⏰ Cache EXPIRED for ${endpoint} - ${machineSerialNo}`);
      cache.current.delete(key);
      return null;
    }

    console.log(`✅ Cache HIT for ${endpoint} - ${machineSerialNo} (age: ${Math.round((now - entry.timestamp) / 1000)}s)`);
    return entry.data;
  }, [generateKey, maxAge]);

  // Set cached data
  const setCachedData = useCallback((
    endpoint: string,
    machineSerialNo: string,
    dateRange: { start: string; end: string },
    shifts: string[],
    data: T
  ): void => {
    if (!machineSerialNo || !dateRange.start || !dateRange.end) return;

    const key = generateKey(endpoint, machineSerialNo, dateRange, shifts);
    
    // Clean up old entries if we exceed maxEntries
    if (cache.current.size >= maxEntries) {
      const oldestKey = cache.current.keys().next().value;
      if (oldestKey) {
        cache.current.delete(oldestKey);
        console.log(`🧹 Cache cleanup: removed oldest entry`);
      }
    }

    cache.current.set(key, {
      data,
      timestamp: Date.now(),
      key
    });

    console.log(`💾 Cache SET for ${endpoint} - ${machineSerialNo} (total entries: ${cache.current.size})`);
  }, [generateKey, maxEntries]);

  // Clear cache for specific machine (useful when machine changes)
  const clearMachineCache = useCallback((machineSerialNo: string) => {
    const keysToDelete: string[] = [];
    cache.current.forEach((_, key) => {
      if (key.includes(`:${machineSerialNo}:`)) {
        keysToDelete.push(key);
      }
    });

    keysToDelete.forEach(key => cache.current.delete(key));
    console.log(`🗑️ Cleared ${keysToDelete.length} cache entries for machine ${machineSerialNo}`);
  }, []);

  // Clear all cache
  const clearAllCache = useCallback(() => {
    const size = cache.current.size;
    cache.current.clear();
    console.log(`🗑️ Cleared all cache (${size} entries)`);
  }, []);

  // Get cache statistics
  const getCacheStats = useCallback(() => {
    const now = Date.now();
    const entries = Array.from(cache.current.values());
    const validEntries = entries.filter(entry => now - entry.timestamp <= maxAge);
    
    return {
      totalEntries: cache.current.size,
      validEntries: validEntries.length,
      expiredEntries: cache.current.size - validEntries.length,
      oldestEntry: entries.length > 0 ? Math.min(...entries.map(e => e.timestamp)) : null,
      newestEntry: entries.length > 0 ? Math.max(...entries.map(e => e.timestamp)) : null
    };
  }, [maxAge]);

  return {
    getCachedData,
    setCachedData,
    clearMachineCache,
    clearAllCache,
    getCacheStats
  };
};

export default useChartCache;
